import React from 'react'
import { IEpiProps, IUserSelections, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  visitedSteps: string[]
  hideAttendeesStep: boolean
  userSelections: IUserSelections
}

interface BreadcrumbStep {
  name: string
  step: string
  number: number
  subSteps?: string[] // Program Selection and Date and Time steps have sub-steps within them
}


export const Breadcrumbs = () => {
  const { epiProps, currentStep, visitedSteps, hideAttendeesStep, userSelections }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!;

  function navigateToStep(step: string) {
    // Only allow navigation to steps that have been visited
    if (visitedSteps.includes(step)) {
      dispatch({
        type: 'updateCurrentStep',
        data: step,
      })
    }
  }

  function navigateToStepTesting(step: string) {
    //For our testing, to be removed later
    dispatch({
      type: 'updateCurrentStep',
      data: step,
    })
  }

  // Create the steps array based on whether the attendees step should be hidden
  const getSteps = (): BreadcrumbStep[] => {
    // Hide the add-on date/time sub-step when add-ons are selected and ALL are product-based (no date/time needed)
    const addOnSelections = userSelections?.userAddOnProgramSelections || []
    const isDateTimeDisabled = addOnSelections.length > 0 && addOnSelections.every(a => a.disableDateTimeSelection || !!a.programSku)

    const baseSteps: BreadcrumbStep[] = [
      {
        name: epiProps.programStepBreadcrumbText,
        step: StepConstants.ProgramStep,
        number: 1,
        subSteps: [StepConstants.AddOnsStep],
      },
      {
        name: epiProps.dateAndTimeStepBreadcrumbText,
        step: StepConstants.DateTimeSpecialtyProgramStep,
        number: 2,
        // Only include DateTimeAddOnProgramStep as a sub-step if date/time is not disabled
        subSteps: isDateTimeDisabled ? [] : [StepConstants.DateTimeAddOnProgramStep],
      }
    ];

    // Add attendees step if not hidden
    if (!hideAttendeesStep) {
      baseSteps.push({
        name: epiProps.attendeesStepBreadcrumbText,
        step: StepConstants.AttendeesStep,
        number: 3,
      });
    }

    // Add review step with appropriate number
    baseSteps.push({
      name: epiProps.reviewStepBreadcrumbText,
      step: StepConstants.ReviewStep,
      number: hideAttendeesStep ? 3 : 4, // Adjust number based on whether attendees step is hidden
    });

    return baseSteps;
  };

  const steps = getSteps();

  const getCurrentMainStep = (): BreadcrumbStep => {
    // First check if currentStep is a main step
    const mainStep = steps.find((step) => step.step === currentStep)
    if (mainStep) return mainStep;

    // If not, check if it's a sub-step
    for (const step of steps) {
      if (step.subSteps?.includes(currentStep)) {
        return step;
      }
    }

    // Default to first step if not found
    return steps[0];
  };

  // Get the current main step
  const currentMainStep = getCurrentMainStep();

  // Helper to determine if a step should be enabled
  const isStepEnabled = (step: BreadcrumbStep): boolean => {
    // Check if main step has been visited
    if (visitedSteps.includes(step.step)) {
      return true;
    }

    // Check if any substeps have been visited
    if (step.subSteps) {
      for (const subStep of step.subSteps) {
        if (visitedSteps.includes(subStep)) {
          return true;
        }
      }
    }

    return false;
  };

  return (
    <div>
      <div className="app-breadcrumbs">
        {steps.map((step, index) => (
          <React.Fragment key={step.step}>
            <button
              className={`app-breadcrumbs-button ${currentMainStep.number === step.number || isStepEnabled(step) ? "active" : ""}`}
              onClick={() => isStepEnabled(step) ? navigateToStep(step.step) : null}
              disabled={!isStepEnabled(step)}

            >
              <span className={`app-breadcrumbs-button-number ${currentMainStep.number === step.number || isStepEnabled(step) ? "app-breadcrumbs-button-number-active" : ""}`}>{step.number}</span>

              <span className="app-breadcrumbs-button-label">
                {step.name}
              </span>

            </button>
            {index < steps.length - 1 && (
              <span className={`app-breadcrumbs-separator ${(currentMainStep.number === step.number || isStepEnabled(step)) &&
                  (currentMainStep.number === steps[index + 1].number || isStepEnabled(steps[index + 1]))
                  ? "active" : ""
                }`}></span>
            )}
          </React.Fragment>
        ))}
      </div>

    </div>
  )
}
